import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, GridLegacy as Grid, Box, Typography, FormControlLabel, Checkbox } from "@mui/material"
import { useReactToPrint } from "react-to-print"
import propTypes from "prop-types";
import { Image as ImageIcon } from "@mui/icons-material";

import styles from "./styles"
import flex from "../../../styles/flex";

import { api, Category, Complaint, ComplaintType } from "../../../logic/api";

import { SessionContext } from "../../../utility/useSession";
import { useDate } from "../../../utility/useDate";
import { getFileUrl } from "../../../utility/utils";

import NotchedCard from "../../../components/cards/NotchedCard/NotchedCard";
import EditableGroup, { EditableGroupType } from "../../../components/editable/EditableGroup/EditableGroup";
import EditableField, { EditableFieldTypes } from "../../../components/editable/EditableField/EditableField";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { IDropDownListItem } from "../../../components/editable/EditableField/fields/DropDownField";
import AlertDialog from "../../../components/notifications/alertDialog/AlertDialog";
import { PrintedForm } from "./PrintedForm";
import { useTranslation } from "react-i18next";
import { ComplaintImagesSection } from "./components/ComplaintImagesSection";
import { useReleaseManagement, ReleaseFlag } from "../../../utility/feature-management";
import { ComplaintImageBox } from "./components/ComplaintImageBox";
import { theme } from "../../../theme";

export interface ComplaintImage {
  clientId: string; // for UI purposes
  uploadedImageId?: number; // for db purposes
  fileName: string;
  name: string;
  isInspectionImage: boolean;
  inspectionId?: number;
  url: string;
}

export const BookingComplaint: React.FunctionComponent<{ complaint: Complaint, show: boolean, onHide: (saved: boolean) => void }> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const [complaint, setComplaint] = useState<Complaint>(props.complaint);
  const [didSave, setDidSave] = useState<boolean>(false);
  const { toFormattedDateTimeString } = useDate();

  const isComplaintImagesEnabled = useReleaseManagement(ReleaseFlag.releaseComplaintImages);

  const [showSendToCustomerRequest, setShowSendToCustomerRequest] = useState<boolean>(false);
  const [showSentToCustomerConfirmation, setShowSentToCustomerConfirmation] = useState<boolean>(false);

  const [showSendToComplaintsDistributionList, setShowSendToComplaintsDistributionList] = useState<boolean>(false);
  const [showSendToComplaintsDistributionConfirmation, setShowSendToComplaintsDistributionConfirmation] = useState<boolean>(false);

  // Image-related state
  const [complaintImages, setComplaintImages] = useState<ComplaintImage[]>([]);
  const [availableInspectionImages, setAvailableInspectionImages] = useState<ComplaintImage[]>([]);
  const [stagedImages, setStagedImages] = useState<Record<string, File>>({});
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<ComplaintImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState<Set<string>>(new Set());
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const imageToDelete = useMemo(() =>
    deletingImageId ? complaintImages.find(img => img.clientId === deletingImageId) : null,
    [deletingImageId, complaintImages]
  );

  // Print image selection modal
  const [showPrintEmailImageModal, setShowPrintEmailImageModal] = useState(false);
  const [selectedPrintEmailImages, setSelectedPrintEmailImages] = useState<ComplaintImage[]>([]);
  const [includeImagesInPrint, setIncludeImagesInPrint] = useState(true);

  // Email image selection modals
  const [showCustomerEmailImageModal, setShowCustomerEmailImageModal] = useState(false);
  const [showDistributionEmailImageModal, setShowDistributionEmailImageModal] = useState(false);
  const [selectedCustomerEmailImages, setSelectedCustomerEmailImages] = useState<ComplaintImage[]>([]);
  const [selectedDistributionEmailImages, setSelectedDistributionEmailImages] = useState<ComplaintImage[]>([]);
  const [includeImagesInCustomerEmail, setIncludeImagesInCustomerEmail] = useState(true);
  const [includeImagesInDistributionEmail, setIncludeImagesInDistributionEmail] = useState(true);

  const update = api.hooks.complaint.createOrUpdate();
  const isNew = !complaint.id;
  const sendToCustomer = api.hooks.complaint.sendToCustomer();
  const sendToDistList = api.hooks.complaint.sendToDistribution();

  // Inspection api hooks for vehicle photos
  const inspectionsList = api.hooks.inspection.list();
  const inspectedAreasList = api.hooks.inspection.listInspectedAreas();

  const printComponentRef = useRef<HTMLDivElement>(null);

  const sendComplaintToCustomer = () => {
    const selectedImages = includeImagesInCustomerEmail ? selectedCustomerEmailImages.map(img => ({
      fileName: img.fileName,
    })) : undefined;

    sendToCustomer.send(session.current.context, {
      id: complaint.id,
      recipient: complaint.email,
      selectedImages: selectedImages
    }, () => {
      setShowSentToCustomerConfirmation(true);
      setShowCustomerEmailImageModal(false);
    });
  }

  const sendComplaintToDistributionList = () => {
    const selectedImages = includeImagesInDistributionEmail ? selectedDistributionEmailImages.map(img => ({
      fileName: img.fileName,
    })) : undefined;

    sendToDistList.send(session.current.context, {
      id: complaint.id,
      selectedImages: selectedImages
    }, () => {
      setShowSendToComplaintsDistributionConfirmation(true);
      setShowDistributionEmailImageModal(false);
    });
  }

  let allowSendingToCustomer = false;

  let complaintsDistributionCategory: Category | undefined;
  let complaintsDistributionCategoryName = "";

  if (session.current.site) {
    if (session.current.site?.preferences.complaints_allow_send_customer_enabled == true) {
      allowSendingToCustomer = true;
    }

    complaintsDistributionCategory = session.current.site.preferences.complaints_distribution_category;
    if (complaintsDistributionCategory) {
      complaintsDistributionCategoryName = complaintsDistributionCategory.categoryName;
    }
  }

  useEffect(() => {
    setComplaint({ ...props.complaint });
    setDidSave(false);

    // Load existing complaint images
    const loadImages = async () => {
      if (props.complaint.id) {
        // Load uploaded images
        const uploadedImages = props.complaint.uploadedImages?.map(img => ({
          clientId: `upload_${img.id}`,
          fileName: img.fileName,
          name: 'Uploaded Image',
          isInspectionImage: false,
          url: getFileUrl(session, img.fileName),
          uploadedImageId: img.id
        })) || [];

        // Load inspection images from the filenames string (these are already linked to the complaint)
        let linkedInspectionImages: ComplaintImage[] = [];
        if (props.complaint.inspectionImageFilenames) {
          try {
            const filenames = JSON.parse(props.complaint.inspectionImageFilenames);
            linkedInspectionImages = filenames.map((filename: string, index: number) => ({
              clientId: `linked_inspection_${index}`,
              fileName: filename,
              name: `Inspection Image ${index + 1}`,
              isInspectionImage: true,
              url: getFileUrl(session, filename)
            }));
          } catch (error) {
            console.error('Error parsing inspection image filenames:', error);
          }
        }

        setComplaintImages([...uploadedImages, ...linkedInspectionImages]);
      } else {
        setComplaintImages([]);
      }
    };

    loadImages();
  }, [props.complaint]);

  const loadInspectionImages = (bookingId: number) => {
    inspectionsList.send(session.current.context, { bookingId }, (inspections) => {
      if (!inspections || inspections.length === 0) {
        return;
      }

      const allImages: ComplaintImage[] = [];

      const processNextInspection = (index: number) => {
        if (index >= inspections.length) {
          setAvailableInspectionImages(allImages);
          return;
        }

        const inspection = inspections[index];
        inspectedAreasList.send(
          session.current.context,
          { inspectionId: inspection.id },
          (areas) => {
            if (areas && areas.length > 0) {
              const inspectionImages: ComplaintImage[] = areas
                .filter(area => area.photoFileName && area.photoFileName.trim() !== '')
                .map(area => ({
                  clientId: `inspection_${area.id}`,
                  fileName: area.photoFileName,
                  name: `${getGridReferenceName(area.gridReference)} - ${new Date(inspection.inspectionDate).toLocaleDateString()}`,
                  isInspectionImage: true,
                  inspectionId: inspection.id,
                  uploadedAt: inspection.inspectionDate,
                  url: getFileUrl(session, area.photoFileName)
                }));

              allImages.push(...inspectionImages);
            }

            processNextInspection(index + 1);
          }
        );
      };

      processNextInspection(0);
    });
  };

  const getGridReferenceName = (gridRef: number): string => {
    const translatedName = t(`complaints.gridReference.${gridRef}`);
    return translatedName !== `complaints.gridReference.${gridRef}` ? translatedName : `Area ${gridRef}`;
  };

  const processFiles = (files: FileList | File[]) => {
    if (!files || files.length === 0) return;

    // Filter for image files only
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      alert('Please select only image files.');
      return;
    }

    if (imageFiles.length < files.length) {
      alert('Some files were skipped because they are not images. Only image files are allowed.');
    }

    // Basic size check for UX - real validation happens on backend
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = imageFiles.filter(file => file.size > maxSize);

    if (oversizedFiles.length > 0) {
      alert(`Some files exceed the 5MB limit and cannot be uploaded.`);
      return;
    }

    // Create preview images for the modal with file references
    const timestamp = Date.now();
    const previewImages: ComplaintImage[] = imageFiles.map((file, index) => ({
      clientId: `staged_${timestamp}_${index}`,
      fileName: '', // Will be set after upload (handled in image_handler.ts file)
      name: file.name,
      isInspectionImage: false,
      url: URL.createObjectURL(file) // Create preview URL
    }));

    // Store files with matching IDs for later upload
    const fileMap = imageFiles.reduce((acc, file, index) => {
      acc[`staged_${timestamp}_${index}`] = file;
      return acc;
    }, {} as Record<string, File>);

    setStagedImages(prev => ({ ...prev, ...fileMap }));
    setComplaintImages(prev => [...prev, ...previewImages]);
  };

  const handleOpenPrintEmailImageModal = () => {
    // Check if there are any images available
    if (complaintImages.length === 0) {
      // No images available, just do the print, just do it.
      handlePrint();
      return;
    }

    // Initialize with all current complaint images selected by default
    setSelectedPrintEmailImages(complaintImages);
    setIncludeImagesInPrint(true);
    setShowPrintEmailImageModal(true);
  };

  const handlePrintEmailImageSelect = (image: ComplaintImage) => {
    setSelectedPrintEmailImages(prev => {
      const isSelected = prev.some(img => img.clientId === image.clientId);
      if (isSelected) {
        return prev.filter(img => img.clientId !== image.clientId);
      } else {
        return [...prev, image];
      }
    });
  };

  const handlePrintEmailWithSelectedImages = () => {
    // Close the modal and proceed with the selected action
    setShowPrintEmailImageModal(false);
    handlePrint();
  };

  const handlePrint = useReactToPrint({
    contentRef: printComponentRef
  });

  const handleOpenImageModal = () => {
    // Load inspection images for this booking if not already loaded
    if (complaint.booking?.id && availableInspectionImages.length === 0) {
      loadInspectionImages(complaint.booking.id);
    }
    // Initialize selectedImages with current complaint images
    const currentSelectedImages = [...complaintImages];
    // For inspection images that are already linked, we need to find their corresponding
    // objects in availableInspectionImages and add them to the selection
    const linkedInspectionImages = complaintImages.filter(img => img.clientId.startsWith('linked_inspection_'));
    linkedInspectionImages.forEach(linkedImg => {
      // Find the corresponding inspection image in availableInspectionImages by fileName
      const correspondingInspectionImg = availableInspectionImages.find(
        availableImg => availableImg.fileName === linkedImg.fileName
      );
      if (correspondingInspectionImg) {
        // Replace the linked image with the available inspection image so selection works
        const linkedIndex = currentSelectedImages.findIndex(img => img.clientId === linkedImg.clientId);
        if (linkedIndex !== -1) {
          currentSelectedImages[linkedIndex] = correspondingInspectionImg;
        }
      }
    });
    setSelectedImages(currentSelectedImages);
    setShowImageModal(true);
  };

  const handleImageSelect = (image: ComplaintImage) => {
    setSelectedImages(prev => {
      const isSelected = prev.some(img => img.clientId === image.clientId);
      if (isSelected) {
        return prev.filter(img => img.clientId !== image.clientId);
      } else {
        return [...prev, image];
      }
    });
  };

  const handleImageModalSave = async () => {
    setIsUploading(true);

    try {
      // For new complaints, save first to get an identifier for image uploads
      let currentComplaint = complaint;
      if (isNew && !currentComplaint.id) {
        const savedComplaint = await handleSave();
        if (!savedComplaint) {
          throw new Error('Failed to save complaint before uploading images');
        }
        currentComplaint = savedComplaint;
      }

      // Upload staged images that are selected
      const stagedSelectedImages = selectedImages.filter(img => img.clientId.startsWith('staged_'));

      // Start upload tracking for selected staged images
      const uploadingIds = new Set(stagedSelectedImages.map(img => img.clientId));
      setUploadingImages(uploadingIds);

      const uploadPromises = stagedSelectedImages.map(async (stagedImage) => {
        // Find the corresponding file
        const file = stagedImages[stagedImage.clientId];
        if (!file) return null;

        try {
          const formData = new FormData();
          formData.append('file', file);

          const headers: Record<string, string> = {
            'filename': file.name,
            'authorization': session.current.context.headers.authorization
          };

          const identifier = currentComplaint.uuid || currentComplaint.id?.toString();
          if (!identifier) {
            throw new Error('No complaint identifier available for upload');
          }

          headers['complaint-identifier'] = identifier;

          const response = await fetch(`${session.current.context.url.replace("/api", "")}/complaints/photos/upload`, {
            method: 'POST',
            headers,
            body: formData
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Upload failed for ${file.name}: ${errorText}`);
          }

          const result = await response.json();

          // Remove from uploading state when done
          setUploadingImages(prev => {
            const newSet = new Set(prev);
            newSet.delete(stagedImage.clientId);
            return newSet;
          });

          return {
            ...stagedImage,
            clientId: `upload_${Date.now()}_${Math.random()}`,
            fileName: result.fileName,
            url: getFileUrl(session, result.fileName),
            uploadedImageId: result.imageId
          };
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          alert(`Failed to upload ${file.name}. Please try again.`);

          // Remove from uploading state on error
          setUploadingImages(prev => {
            const newSet = new Set(prev);
            newSet.delete(stagedImage.clientId);
            return newSet;
          });

          return null;
        }
      });

      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter(Boolean) as ComplaintImage[];

      // Separate selected images by type
      const selectedUploadedImages = selectedImages.filter(img => !img.clientId.startsWith('staged_') && !img.isInspectionImage);
      const selectedInspectionImages = selectedImages.filter(img => img.isInspectionImage);

      // Combine uploaded images (existing + newly uploaded)
      const allUploadedImages = [...selectedUploadedImages, ...successfulUploads];

      // Get currently linked inspection image filenames to avoid duplicates
      const currentlyLinkedFilenames = new Set(
        complaintImages
          .filter(img => img.clientId.startsWith('linked_inspection_'))
          .map(img => img.fileName)
      );

      // Only include inspection images that aren't already linked
      const newlyLinkedInspectionImages = selectedInspectionImages.filter(
        img => !currentlyLinkedFilenames.has(img.fileName)
      );

      // Keep existing linked inspection images and add only new ones
      const existingLinkedInspectionImages = complaintImages.filter(img => img.clientId.startsWith('linked_inspection_'));
      const finalImages = [...allUploadedImages, ...existingLinkedInspectionImages, ...newlyLinkedInspectionImages];

      setComplaintImages(finalImages);

      // Clean up staged images and their preview URLs
      stagedSelectedImages.forEach(img => {
        if (img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });
      setStagedImages({});

      // Update the complaint with image metadata (for both new and existing complaints)
      // Get all inspection image filenames (existing + newly selected)
      const allInspectionFilenames = [
        ...existingLinkedInspectionImages.map(img => img.fileName),
        ...newlyLinkedInspectionImages.map(img => img.fileName)
      ];

      // Update only the inspection image filenames - uploaded images are handled separately by the upload endpoint
      const updatedComplaint: Complaint = {
        ...currentComplaint,
        inspectionImageFilenames: allInspectionFilenames.length > 0 ? JSON.stringify(allInspectionFilenames) : "[]"
      };
      await handleSave(updatedComplaint);

    } catch (error) {
      console.error('Error during image upload:', error);
      alert('Some images failed to upload. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadingImages(new Set());
      setShowImageModal(false);
      setSelectedImages([]);
    }
  };

  const handleImageModalCancel = () => {
    // Clean up staged images and their preview URLs
    complaintImages.forEach(img => {
      if (img.clientId.startsWith('staged_') && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
    });

    // Remove staged images from complaint images
    setComplaintImages(prev => prev.filter(img => !img.clientId.startsWith('staged_')));
    setStagedImages({});
    setUploadingImages(new Set());
    setShowImageModal(false);
    setSelectedImages([]);
  };

  const removeComplaintImage = (imageToRemove: ComplaintImage) => {
    setDeletingImageId(imageToRemove.clientId);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirmation = async () => {
    if (!imageToDelete) return;

    // If it's a staged image, just remove from UI
    if (imageToDelete.clientId.startsWith('staged_')) {
      // Clean up preview URL
      if (imageToDelete.url.startsWith('blob:')) {
        URL.revokeObjectURL(imageToDelete.url);
      }
      setComplaintImages(prev => prev.filter(img => img.clientId !== imageToDelete.clientId));
      setShowDeleteConfirmation(false);
      setDeletingImageId(null);
      return;
    }

    // If it's an uploaded image, delete it from database
    if (imageToDelete.uploadedImageId) {
      try {
        // Use UUID if available, otherwise use ID - api handles both
        const identifier = complaint.uuid || complaint.id?.toString();
        if (!identifier) {
          throw new Error('No complaint identifier available for deletion');
        }

        const response = await fetch(`${session.current.context.url.replace("/api", "")}/complaints/${identifier}/photos/${imageToDelete.uploadedImageId}`, {
          method: 'DELETE',
          headers: {
            'authorization': session.current.context.headers.authorization
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete image from server');
        }

        // Remove from UI only after successful deletion
        setComplaintImages(prev => prev.filter(img => img.clientId !== imageToDelete.clientId));
      } catch (error) {
        console.error('Failed to delete image:', error);
        alert('Failed to delete image. Please try again.');
      }
    } else {
      // Inspection image - remove from UI first
      setComplaintImages(prev => prev.filter(img => img.clientId !== imageToDelete.clientId));

      // If it's ANY inspection image (inspection_ or linked_inspection_), update the complaint's inspectionImageFilenames
      if (imageToDelete.isInspectionImage) {
        // Calculate remaining inspection images after removing this one
        // Filter out the image we're deleting from the current complaint images
        const imagesAfterDeletion = complaintImages.filter(img => img.clientId !== imageToDelete.clientId);
        const remainingLinkedInspectionImages = imagesAfterDeletion.filter(
          img => img.isInspectionImage && (img.clientId.startsWith('linked_inspection_') || img.clientId.startsWith('inspection_'))
        );

        const inspectionImageFilenames = remainingLinkedInspectionImages.length > 0
          ? JSON.stringify(remainingLinkedInspectionImages.map(img => img.fileName))
          : "[]";

        const updatedComplaint: Complaint = {
          ...complaint,
          inspectionImageFilenames: inspectionImageFilenames
        };

        // Save the updated complaint
        handleSave(updatedComplaint);
      }
    }

    setShowDeleteConfirmation(false);
    setDeletingImageId(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setDeletingImageId(null);
  };

  const handleSave = async (changes?: Complaint): Promise<Complaint | null> => {
    const complaintToSave = changes || complaint;

    if (isNew) {
      // For new complaints, we need to actually save them
      return new Promise((resolve) => {
        update.send(session.current.context, complaintToSave, (updated) => {
          if (updated) {
            if (updated.err) {
              alert(updated.err);
              resolve(null);
            } else {
              setComplaint(updated.complaint);
              setDidSave(true);
              resolve(updated.complaint);
            }
          } else {
            resolve(null);
          }
        });
      });
    }

    // For existing complaints
    return new Promise((resolve) => {
      update.send(session.current.context, complaintToSave, (updated) => {
        if (updated) {
          if (updated.err) {
            alert(updated.err);
          }

          setComplaint(updated.complaint);
          setDidSave(true);
          resolve(updated.complaint);
        } else {
          resolve(null);
        }
      });
    });
  };

  const handleOpenCustomerEmailDialog = () => {
    // Show image selection modal if images are available, otherwise show simple email dialog
    if (isComplaintImagesEnabled && complaintImages.length > 0) {
      setSelectedCustomerEmailImages(complaintImages);
      setIncludeImagesInCustomerEmail(true);
      setShowCustomerEmailImageModal(true);
    } else {
      setShowSendToCustomerRequest(true);
    }
  };

  const handleOpenDistributionEmailDialog = () => {
    // Show image selection modal if images are available, otherwise show simple email dialog
    if (isComplaintImagesEnabled && complaintImages.length > 0) {
      setSelectedDistributionEmailImages(complaintImages);
      setIncludeImagesInDistributionEmail(true);
      setShowDistributionEmailImageModal(true);
    } else {
      setShowSendToComplaintsDistributionList(true);
    }
  };

  const handleCustomerEmailImageSelect = (image: ComplaintImage) => {
    setSelectedCustomerEmailImages(prev => {
      const isSelected = prev.some(img => img.clientId === image.clientId);
      if (isSelected) {
        return prev.filter(img => img.clientId !== image.clientId);
      } else {
        return [...prev, image];
      }
    });
  };

  const handleDistributionEmailImageSelect = (image: ComplaintImage) => {
    setSelectedDistributionEmailImages(prev => {
      const isSelected = prev.some(img => img.clientId === image.clientId);
      if (isSelected) {
        return prev.filter(img => img.clientId !== image.clientId);
      } else {
        return [...prev, image];
      }
    });
  };

  const handleCustomerEmailWithSelectedImages = () => {
    setShowCustomerEmailImageModal(false);
    setShowSendToCustomerRequest(true);
  };

  const handleDistributionEmailWithSelectedImages = () => {
    setShowDistributionEmailImageModal(false);
    setShowSendToComplaintsDistributionList(true);
  };

  return <Dialog open={props.show} fullWidth={true} maxWidth="sm"  >
    <DialogTitle>
      <div style={{ textAlign: "center", fontWeight: "700" }}>
        {isNew ? t("general.new") : ""} {complaint.complaintType == ComplaintType.service ? t("complaints.service") : complaint.complaintType == ComplaintType.damage ? t("complaints.damage") : t("complaints.damageAndService")}
      </div>
    </DialogTitle>
    <DialogContent>
      <ProgressDialog status={update.status}></ProgressDialog>

      <ErrorDialog err={update.err}></ErrorDialog>
      <ProgressDialog status={sendToCustomer.status}></ProgressDialog>
      <ErrorDialog err={sendToCustomer.err}></ErrorDialog>

      <ProgressDialog status={sendToDistList.status}></ProgressDialog>
      <ErrorDialog err={sendToDistList.err}></ErrorDialog>

      {/* Send to Customer Dialog */}
      <Dialog open={showSendToCustomerRequest} fullWidth maxWidth="sm">
        <DialogTitle>{t("booking.sendCopy")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("booking.sendCopyConfirmation", { email: complaint.email })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSendToCustomerRequest(false);
            }}
          >
            {t("general.cancel")}
          </Button>
          <Button
            onClick={() => {
              setShowSendToCustomerRequest(false);
              sendComplaintToCustomer();
            }}
          >
            <b>{t("general.send")}</b>
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        title={t("booking.sentTo.title")}
        message={t("booking.sentTo.message")}
        show={showSentToCustomerConfirmation}
        onClose={() => {
          setShowSentToCustomerConfirmation(false);
        }}
      ></AlertDialog>

      {/* Send to Distribution List Dialog */}
      <Dialog open={showSendToComplaintsDistributionList} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("booking.sendCopyTo", { name: `'${complaintsDistributionCategoryName}' distribution list` })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("booking.sendCopyToConfirmation")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSendToComplaintsDistributionList(false);
            }}
          >
            {t("general.cancel")}
          </Button>
          <Button
            onClick={() => {
              setShowSendToComplaintsDistributionList(false);
              sendComplaintToDistributionList();
            }}
          >
            <b>{t("general.send")}</b>
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        title={t("booking.sentDistribution.title")}
        message={t("booking.sentDistribution.message")}
        show={showSendToComplaintsDistributionConfirmation}
        onClose={() => {
          setShowSendToComplaintsDistributionConfirmation(false);
        }}
      ></AlertDialog>

      {/* Image Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirmation} fullWidth maxWidth="sm">
        <DialogTitle>{t("general.confirm")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("complaints.images.confirmDelete")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="secondary">
            {t("general.cancel")}
          </Button>
          <Button onClick={handleDeleteConfirmation} color="error" variant="contained">
            {t("general.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <div style={flex.flexRows}>
        <div style={{ marginBottom: 40, width: "50%" }}>
          <div style={flex.flexRows}>
            <div style={{ ...flex.flexFill, textAlign: "right" }}>
              <div style={{ paddingRight: 20 }}>{t("general.created")}:</div>
            </div>
            <div style={flex.flexFill}>
              {toFormattedDateTimeString(new Date(complaint.complaintDate))}
            </div>
          </div>
          <div style={styles.spacer}></div>
          <div style={flex.flexRows}>
            <div style={{ ...flex.flexFill, textAlign: "right" }}>
              <div style={{ paddingRight: 20 }}>{t("booking.labels.loggedBy")}:</div>
            </div>
            <div style={flex.flexFill}>
              {complaint.createdBy.firstName} {complaint.createdBy.lastName}
            </div>
          </div>
          <div style={styles.spacer}></div>
          {(complaint.deviceSerialNo != null && complaint.deviceSerialNo != "") && (
            <div style={flex.flexRows}>
              <div style={{ ...flex.flexFill, textAlign: "right" }}>
                <div style={{ paddingRight: 20 }}>{t("booking.labels.device")}:</div>
              </div>
              <div style={flex.flexFill}>
                {complaint.deviceSerialNo}
              </div>
            </div>
          )}
          <div style={styles.spacer}></div>
          <div style={flex.flexRows}>
            <div style={{ ...flex.flexFill, textAlign: "right" }}>
              <div style={{ paddingRight: 20 }}>{t("booking.labels.booking")}:</div>
            </div>
            <div style={flex.flexFill}>
              {complaint.booking?.ref || ""}
            </div>
          </div>
        </div>
        <div style={{ marginBottom: 40, width: "40%" }}>
          <div style={flex.flexRows}>
            <div style={{ ...flex.flexFill, textAlign: "right" }}>
              <div style={{ paddingRight: 20 }}>{t("booking.labels.vehicle")}:</div>
            </div>
            <div style={flex.flexFill}>
              {complaint.booking?.vehicle?.make || ""} {complaint.booking?.vehicle?.model || ""}
            </div>
          </div>
          <div style={styles.spacer}></div>
          <div style={flex.flexRows}>
            <div style={{ ...flex.flexFill, textAlign: "right" }}>
              <div style={{ paddingRight: 20 }}>{t("booking.labels.colour")}:</div>
            </div>
            <div style={flex.flexFill}>
              {complaint.booking?.vehicle?.colour || ""}
            </div>
          </div>
          <div style={styles.spacer}></div>
          <div style={flex.flexRows}>
            <div style={{ ...flex.flexFill, textAlign: "right" }}>
              <div style={{ paddingRight: 20 }}>{t("booking.labels.registration")}:</div>
            </div>
            <div style={flex.flexFill}>
              {complaint.booking?.vehicle?.registration || ""}
            </div>
          </div>
        </div>
      </div>
      <NotchedCard label={"Customer"}>
        <div style={styles.antiSpacer}></div>
        <EditableGroup<Complaint>
          id="customer"
          testId="complaint-customer-group"
          editType={isNew ? EditableGroupType.alwaysEditing : EditableGroupType.switchableEditing}
          data={complaint}
          onDataChanged={async (updatedData) => {
            if (isNew) {
              setComplaint(updatedData);
              return true;
            } else {
              const result = await handleSave(updatedData);
              return !!result;
            }
          }}
        >
          <Grid container>
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.customerName")}
              onProvide={(data) => data.leadPersonName}
              onChange={(data, value) => data.leadPersonName = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.email")}
              onProvide={(data) => data.email}
              onChange={(data, value) => data.email = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.addressLine1")}
              onProvide={(data) => data.address1}
              onChange={(data, value) => data.address1 = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.phoneWork")}
              onProvide={(data) => data.phoneWork}
              onChange={(data, value) => data.phoneWork = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.addressLine2")}
              onProvide={(data) => data.address2}
              onChange={(data, value) => data.address2 = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.phoneMobile")}
              onProvide={(data) => data.phoneMobile}
              onChange={(data, value) => data.phoneMobile = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.townCity")}
              onProvide={(data) => data.townCity}
              onChange={(data, value) => data.townCity = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.phoneHome")}
              onProvide={(data) => data.phoneHome}
              onChange={(data, value) => data.phoneHome = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.county")}
              onProvide={(data) => data.county}
              onChange={(data, value) => data.county = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.text}
              label={t("input.labels.postcode")}
              onProvide={(data) => data.postCode}
              onChange={(data, value) => data.postCode = value}
            />
          </Grid>

        </EditableGroup>
      </NotchedCard>
      <div style={styles.spacer}></div>
      <NotchedCard label={t("card.labels.complaintDetails")} >
        <div style={styles.antiSpacer}></div>
        <EditableGroup<Complaint>
          id="complaint"
          testId="complaint-details-group"
          editType={isNew ? EditableGroupType.alwaysEditing : EditableGroupType.switchableEditing}
          data={complaint}
          onDataChanged={async (updatedData) => {
            if (isNew) {
              setComplaint(updatedData);
              return true;
            } else {
              const result = await handleSave(updatedData);
              return !!result;
            }
          }}
        >
          <Grid container>
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.textMultiLineFull}
              label={t("card.labels.complaintDetails")}
              onProvide={(data) => data.complaintDetails}
              onChange={(data, value) => data.complaintDetails = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.textMultiLineFull}
              label={t("input.labels.verbalComments")}
              onProvide={(data) => data.verbalComments}
              onChange={(data, value) => data.verbalComments = value}
            />
            <EditableField<Complaint, string>
              inputType={EditableFieldTypes.textMultiLineFull}
              label={t("input.labels.staffComments")}
              onProvide={(data) => data.staffComments}
              onChange={(data, value) => data.staffComments = value}
            />
          </Grid>
        </EditableGroup>
      </NotchedCard>

      {(complaint.complaintType == ComplaintType.damage || complaint.complaintType == ComplaintType.serviceAndDamage) && (
        <div style={styles.noPageBreak}>
          <div style={styles.spacer}></div>
          <NotchedCard label={t("card.labels.questions")}>
            <div style={styles.antiSpacer}></div>
            <EditableGroup<Complaint>
              id="questions"
              testId="complaint-questions-group"
              editType={isNew ? EditableGroupType.alwaysEditing : EditableGroupType.switchableEditing}
              data={complaint}
              onDataChanged={async (updatedData) => {
                if (isNew) {
                  setComplaint(updatedData);
                  return true;
                } else {
                  const result = await handleSave(updatedData);
                  return !!result;
                }
              }}
            >
              <Grid container>
                <EditableField<Complaint, IDropDownListItem>
                  inputType={EditableFieldTypes.terminalField}
                  label={t("input.labels.freshDamage")}
                  onProvide={(data) => {
                    return {
                      name: data.damageLooksFresh,
                      value: data.damageLooksFresh,
                    };
                  }}
                  onChange={(data, item) => {
                    data.damageLooksFresh = item.value || "";
                  }}
                  onRequestData={async () => {
                    return [
                      {
                        name: "",
                        value: ""
                      },
                      {
                        name: t("general.yes"),
                        value: t("general.yes")
                      },
                      {
                        name: t("general.no"),
                        value: t("general.no")
                      }
                    ];
                  }}
                />
                <EditableField<Complaint, IDropDownListItem>
                  inputType={EditableFieldTypes.terminalField}
                  label={t("input.labels.damageAdjacent")}
                  onProvide={(data) => {
                    return {
                      name: data.damageAdjacent,
                      value: data.damageAdjacent,
                    };
                  }}
                  onChange={(data, item) => {
                    data.damageAdjacent = item.value || "";
                  }}
                  onRequestData={async () => {
                    return [
                      {
                        name: "",
                        value: ""
                      },
                      {
                        name: t("general.yes"),
                        value: t("general.yes")
                      },
                      {
                        name: t("general.no"),
                        value: t("general.no")
                      }
                    ];
                  }}
                />
                <EditableField<Complaint, IDropDownListItem>
                  inputType={EditableFieldTypes.terminalField}
                  label={t("input.labels.rustDamage")}
                  onProvide={(data) => {
                    return {
                      name: data.rustAroundDamage,
                      value: data.rustAroundDamage,
                    };
                  }}
                  onChange={(data, item) => {
                    data.rustAroundDamage = item.value || "";
                  }}
                  onRequestData={async () => {
                    return [
                      {
                        name: "",
                        value: ""
                      },
                      {
                        name: t("general.yes"),
                        value: t("general.yes")
                      },
                      {
                        name: t("general.no"),
                        value: t("general.no")
                      }
                    ];
                  }}
                />
                <EditableField<Complaint, IDropDownListItem>
                  inputType={EditableFieldTypes.terminalField}
                  label={t("input.labels.obviousDamage")}
                  onProvide={(data) => {
                    return {
                      name: data.damageObvious,
                      value: data.damageObvious,
                    };
                  }}
                  onChange={(data, item) => {
                    data.damageObvious = item.value || "";
                  }}
                  onRequestData={async () => {
                    return [
                      {
                        name: "",
                        value: ""
                      },
                      {
                        name: t("general.yes"),
                        value: t("general.yes")
                      },
                      {
                        name: t("general.no"),
                        value: t("general.no")
                      }
                    ];
                  }}
                />
                <EditableField<Complaint, IDropDownListItem>
                  inputType={EditableFieldTypes.terminalField}
                  label={t("input.labels.carparkDamage")}
                  onProvide={(data) => {
                    return {
                      name: data.damageHappendInCarPark,
                      value: data.damageHappendInCarPark,
                    };
                  }}
                  onChange={(data, item) => {
                    data.damageHappendInCarPark = item.value || "";
                  }}
                  onRequestData={async () => {
                    return [
                      {
                        name: "",
                        value: ""
                      },
                      {
                        name: t("general.yes"),
                        value: t("general.yes")
                      },
                      {
                        name: t("general.possibly"),
                        value: t("general.possibly")
                      },
                      {
                        name: t("general.no"),
                        value: t("general.no")
                      }
                    ];
                  }}
                />
                <EditableField<Complaint, IDropDownListItem>
                  inputType={EditableFieldTypes.terminalField}
                  label={t("input.labels.generalCondition")}
                  onProvide={(data) => {
                    return {
                      name: data.generalCondition,
                      value: data.generalCondition,
                    };
                  }}
                  onChange={(data, item) => {
                    data.generalCondition = item.value || "";
                  }}
                  onRequestData={async () => {
                    return [
                      {
                        name: "",
                        value: ""
                      },
                      {
                        name: t("general.new"),
                        value: t("general.new")
                      },
                      {
                        name: t("general.good"),
                        value: t("general.good")
                      },
                      {
                        name: t("general.poor"),
                        value: t("general.poor")
                      },
                      {
                        name: t("general.veryBad"),
                        value: t("general.veryBad")
                      }
                    ];
                  }}
                />
                <EditableField<Complaint, IDropDownListItem>
                  inputType={EditableFieldTypes.terminalField}
                  label={t("input.labels.customerAttitude")}
                  onProvide={(data) => {
                    return {
                      name: data.attitude,
                      value: data.attitude,
                    };
                  }}
                  onChange={(data, item) => {
                    data.attitude = item.value || "";
                  }}
                  onRequestData={async () => {
                    return [
                      {
                        name: "",
                        value: ""
                      },
                      {
                        name: t("general.angry"),
                        value: t("general.angry")
                      },
                      {
                        name: t("general.annoyed"),
                        value: t("general.annoyed")
                      },
                      {
                        name: t("general.calm"),
                        value: t("general.calm")
                      }
                    ];
                  }}
                />
              </Grid>
            </EditableGroup>
          </NotchedCard>
        </div>
      )}

      {/* Images Section */}
      {isComplaintImagesEnabled && (
        <>
          <div style={styles.spacer}></div>
          <NotchedCard label="Images">
            {/* <div style={styles.antiSpacer}></div> */}
            <ComplaintImagesSection
              complaintImages={complaintImages}
              availableInspectionImages={availableInspectionImages}
              selectedImages={selectedImages}
              uploadingImages={uploadingImages}
              deletingImageId={deletingImageId}
              isUploading={isUploading}
              onOpenImageModal={handleOpenImageModal}
              onImageModalSave={handleImageModalSave}
              onImageModalCancel={handleImageModalCancel}
              onImageSelect={handleImageSelect}
              onRemoveImage={removeComplaintImage}
              onFilesSelected={processFiles}
              showImageModal={showImageModal}
            />
          </NotchedCard>
        </>
      )}

      {/* Print Selection Modal */}
      <Dialog
        open={showPrintEmailImageModal}
        fullWidth
        maxWidth={includeImagesInPrint ? "lg" : "sm"}
        onClose={() => setShowPrintEmailImageModal(false)}
      >
        <DialogTitle>
          {t('complaints.images.selectImagesFor', { action: t('input.labels.print') })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 3 }}>
            {complaintImages.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeImagesInPrint}
                      onChange={(e) => {
                        setIncludeImagesInPrint(e.target.checked);
                        if (e.target.checked) {
                          setSelectedPrintEmailImages(complaintImages);
                        } else {
                          setSelectedPrintEmailImages([]);
                        }
                      }}
                      sx={{
                        color: theme.palette.brand.main,
                        '&.Mui-checked': {
                          color: theme.palette.brand.main,
                        },
                      }}
                    />
                  }
                  label={t("complaints.images.includePhotos")}
                />
              </Box>
            )}

            {includeImagesInPrint && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('complaints.images.includedInPrint')}
                </Typography>

                <Grid container spacing={2}>
                  {complaintImages.map((image) => (
                    <Grid item xs={12} sm={6} md={4} key={image.clientId}>
                      <ComplaintImageBox
                        image={image}
                        onClick={() => handlePrintEmailImageSelect(image)}
                      >
                        {/* Selection Checkbox */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1
                          }}
                        >
                          <Checkbox
                            checked={selectedPrintEmailImages.some(img => img.clientId === image.clientId)}
                            onChange={() => handlePrintEmailImageSelect(image)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.9)',
                              borderRadius: '50%',
                              padding: '4px',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 1)',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: 20
                              },
                              color: theme.palette.brand.main,
                              '&.Mui-checked': {
                                color: theme.palette.brand.main,
                              }
                            }}
                          />
                        </Box>
                      </ComplaintImageBox>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {complaintImages.length === 0 && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
                color: 'text.secondary'
              }}>
                <ImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">
                  {t('complaints.images.noImagesAttachedToComplaint')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowPrintEmailImageModal(false)}
          >
            {t('general.cancel')}
          </Button>
          {!includeImagesInPrint && (
            <Button
              onClick={handlePrintEmailWithSelectedImages}
              sx={{ fontWeight: "bold" }}
            >
              {t("input.labels.print")}
            </Button>
          )}
          {includeImagesInPrint && selectedPrintEmailImages.length === 0 && (
            <Button
              onClick={handlePrintEmailWithSelectedImages}
              sx={{ fontWeight: "bold" }}
            >
              {t('complaints.images.printWithoutImages')}
            </Button>
          )}
          {includeImagesInPrint && selectedPrintEmailImages.length > 0 && (
            <Button
              onClick={handlePrintEmailWithSelectedImages}
              sx={{ fontWeight: "bold" }}
            >
              {t('complaints.images.printWithImages', { count: selectedPrintEmailImages.length })}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Customer Email Image Selection Modal */}
      <Dialog
        open={showCustomerEmailImageModal}
        fullWidth
        maxWidth={includeImagesInCustomerEmail ? "lg" : "sm"}
        onClose={() => setShowCustomerEmailImageModal(false)}
      >
        <DialogTitle>
          {t('complaints.images.selectImagesFor', { action: t('booking.emailCopy') })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 3 }}>
            {complaintImages.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeImagesInCustomerEmail}
                      onChange={(e) => {
                        setIncludeImagesInCustomerEmail(e.target.checked);
                        if (e.target.checked) {
                          setSelectedCustomerEmailImages(complaintImages);
                        } else {
                          setSelectedCustomerEmailImages([]);
                        }
                      }}
                      sx={{
                        color: theme.palette.brand.main,
                        '&.Mui-checked': {
                          color: theme.palette.brand.main,
                        },
                      }}
                    />
                  }
                  label={t("complaints.images.includePhotos")}
                />
              </Box>
            )}

            {includeImagesInCustomerEmail && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('complaints.images.includedInEmail')}
                </Typography>

                <Grid container spacing={2}>
                  {complaintImages.map((image) => (
                    <Grid item xs={12} sm={6} md={4} key={image.clientId}>
                      <ComplaintImageBox
                        image={image}
                        onClick={() => handleCustomerEmailImageSelect(image)}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1
                          }}
                        >
                          <Checkbox
                            checked={selectedCustomerEmailImages.some(img => img.clientId === image.clientId)}
                            onChange={() => handleCustomerEmailImageSelect(image)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.9)',
                              borderRadius: '50%',
                              padding: '4px',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 1)',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: 20
                              },
                              color: theme.palette.brand.main,
                              '&.Mui-checked': {
                                color: theme.palette.brand.main,
                              }
                            }}
                          />
                        </Box>
                      </ComplaintImageBox>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {complaintImages.length === 0 && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
                color: 'text.secondary'
              }}>
                <ImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">
                  {t('complaints.images.noImagesAttachedToComplaint')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCustomerEmailImageModal(false)}
          >
            {t('general.cancel')}
          </Button>
          {!includeImagesInCustomerEmail && (
            <Button
              onClick={handleCustomerEmailWithSelectedImages}
              sx={{ fontWeight: "bold" }}
            >
              {t("general.send")}
            </Button>
          )}
          {includeImagesInCustomerEmail && selectedCustomerEmailImages.length === 0 && (
            <Button
              onClick={handleCustomerEmailWithSelectedImages}
              sx={{ fontWeight: "bold" }}
            >
              {t('complaints.images.emailWithoutImages')}
            </Button>
          )}
          {includeImagesInCustomerEmail && selectedCustomerEmailImages.length > 0 && (
            <Button
              onClick={handleCustomerEmailWithSelectedImages}
              sx={{ fontWeight: "bold" }}
            >
              {t('complaints.images.emailWithImages', { count: selectedCustomerEmailImages.length })}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Distribution Email Image Selection Modal */}
      <Dialog
        open={showDistributionEmailImageModal}
        fullWidth
        maxWidth={includeImagesInDistributionEmail ? "lg" : "sm"}
        onClose={() => setShowDistributionEmailImageModal(false)}
      >
        <DialogTitle>
          {t('complaints.images.selectImagesFor', { action: t("booking.emailTo", { name: complaintsDistributionCategoryName }) })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 3 }}>
            {complaintImages.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeImagesInDistributionEmail}
                      onChange={(e) => {
                        setIncludeImagesInDistributionEmail(e.target.checked);
                        if (e.target.checked) {
                          setSelectedDistributionEmailImages(complaintImages);
                        } else {
                          setSelectedDistributionEmailImages([]);
                        }
                      }}
                      sx={{
                        color: theme.palette.brand.main,
                        '&.Mui-checked': {
                          color: theme.palette.brand.main,
                        },
                      }}
                    />
                  }
                  label={t("complaints.images.includePhotos")}
                />
              </Box>
            )}

            {includeImagesInDistributionEmail && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('complaints.images.includedInEmail')}
                </Typography>

                <Grid container spacing={2}>
                  {complaintImages.map((image) => (
                    <Grid item xs={12} sm={6} md={4} key={image.clientId}>
                      <ComplaintImageBox
                        image={image}
                        onClick={() => handleDistributionEmailImageSelect(image)}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1
                          }}
                        >
                          <Checkbox
                            checked={selectedDistributionEmailImages.some(img => img.clientId === image.clientId)}
                            onChange={() => handleDistributionEmailImageSelect(image)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.9)',
                              borderRadius: '50%',
                              padding: '4px',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 1)',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: 20
                              },
                              color: theme.palette.brand.main,
                              '&.Mui-checked': {
                                color: theme.palette.brand.main,
                              }
                            }}
                          />
                        </Box>
                      </ComplaintImageBox>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {complaintImages.length === 0 && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
                color: 'text.secondary'
              }}>
                <ImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">
                  {t('complaints.images.noImagesAttachedToComplaint')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDistributionEmailImageModal(false)}
          >
            {t('general.cancel')}
          </Button>
          {!includeImagesInDistributionEmail && (
            <Button
              onClick={handleDistributionEmailWithSelectedImages}
              sx={{ fontWeight: "bold" }}
            >
              {t("general.send")}
            </Button>
          )}
          {includeImagesInDistributionEmail && selectedDistributionEmailImages.length === 0 && (
            <Button
              onClick={handleDistributionEmailWithSelectedImages}
              sx={{ fontWeight: "bold" }}
            >
              {t('complaints.images.emailWithoutImages')}
            </Button>
          )}
          {includeImagesInDistributionEmail && selectedDistributionEmailImages.length > 0 && (
            <Button
              onClick={handleDistributionEmailWithSelectedImages}
              sx={{ fontWeight: "bold" }}
            >
              {t('complaints.images.emailWithImages', { count: selectedDistributionEmailImages.length })}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </DialogContent>

    {/* Main Dialog Actions */}
    <DialogActions>
      {(!isNew && complaintsDistributionCategory) && (
        <Button onClick={handleOpenDistributionEmailDialog} color="secondary">
          {t("booking.emailTo", { name: complaintsDistributionCategoryName })}
        </Button>
      )}
      {(allowSendingToCustomer && !isNew && complaint.email) && (
        <Button onClick={handleOpenCustomerEmailDialog} color="secondary">
          {t("booking.emailCopy")}
        </Button>
      )}
      {!isNew && (
        <Button onClick={() => isComplaintImagesEnabled ? handleOpenPrintEmailImageModal() : handlePrint()} color="secondary">{t("input.labels.print")}</Button>
      )}
      <Button onClick={() => props.onHide(didSave)} color="secondary">{t("general.close")}</Button>
      {isNew && (
        <Button onClick={() => handleSave()} color="secondary">{t("input.labels.createComplaint")}</Button>
      )}
    </DialogActions>

    {/* Print Component */}
    <div style={{ "display": "none" }}>
      <div ref={printComponentRef}>
        <PrintedForm
          complaint={complaint}
          complaintType={complaint.complaintType == ComplaintType.service ? t("complaints.service") : complaint.complaintType == ComplaintType.damage ? t("complaints.damage") : t("complaints.damageAndService")}
          selectedImages={includeImagesInPrint ? selectedPrintEmailImages.map(img => ({ ...img, id: img.clientId })) : []}
        />
      </div>
    </div>
  </Dialog>
};

BookingComplaint.propTypes = {
  complaint: propTypes.any.isRequired,
  show: propTypes.bool.isRequired,
  onHide: propTypes.any.isRequired
};